import { DOWN_ARROW, ESCAPE_KEY, TAB_KEY, UP_ARROW } from 'constants/keyCodes';
import React, { cloneElement, FC, useCallback, useRef, useState } from 'react';
import ReactDOMServer from 'react-dom/server';
import * as Popover from '@radix-ui/react-popover';
import noop from 'utils/noop';
import { useDropdownPopoverAlignment } from 'ui/utility/hooks/useDropdownPopoverAlignment';
import { BUTTON_SIZES } from '../button/constants';
import {
  Divider,
  StyledActionMenu,
  StyledOption,
  StyledOptionDescription,
  StyledOptions,
  TitleOption,
} from './ActionMenu.styles';
import {
  ActionMenuOption,
  ActionMenuOptionProps,
  AllowedActionMenuElements,
  LinkOption,
  MENU_ALIGNMENTS,
  MENU_DIRECTIONS,
} from './types';

const FragmentWrapper: FC<React.PropsWithChildren<unknown>> = ({ children }) => (
  <React.Fragment>{children}</React.Fragment>
);

export type Props = {
  /** Controls if the floating menu is aligned to the right or left of the trigger */
  align?: MENU_ALIGNMENTS.LEFT | MENU_ALIGNMENTS.RIGHT;
  /**
   * Automatically change the dropdown `verticalAlignment` and `horizontalAlignment`
   * to avoid collisions with the viewport.
   * When this is on, `verticalAlignment` and `horizontalAlignment` are used as default alignments.
   */
  automaticallyAvoidCollisions?: boolean;
  children: React.ReactNode;
  /** Controls if the floating menu opens to the top or bottom of the trigger */
  direction?: MENU_DIRECTIONS;
  /** The element to use as the trigger, MUST BE A SINGLE NODE */
  id?: string;
  /** Whether to prevent action menu from being toggled open/closed */
  isDisabled?: boolean;
  /** the options to show in the floating menu */
  options: ActionMenuOption[];
  showArrow?: boolean;
  size?: BUTTON_SIZES;
  style?: React.CSSProperties;
};

export const ActionMenu: FC<React.PropsWithChildren<Props>> = ({
  align = MENU_ALIGNMENTS.LEFT,
  automaticallyAvoidCollisions = true,
  children,
  direction = MENU_DIRECTIONS.BOTTOM,
  id,
  isDisabled = false,
  options = [],
  ...props
}: Props) => {
  const [isOpen, setIsOpen] = useState(false);
  const menuEl = useRef(null);
  const optionEl = useRef(null);

  const toggle = () => {
    if (!isDisabled) setIsOpen(!isOpen);
  };

  const handleKeyPress = useCallback((e: any) => {
    const { keyCode } = e;

    const clickables = Array.from((optionEl as any).current.querySelectorAll('[tabindex="0"]'));

    const focusedOptionIndex = clickables.findIndex((item) => item === document.activeElement);
    const [firstOption, lastOption] = [
      clickables[0] as HTMLElement,
      clickables[clickables.length - 1] as HTMLElement,
    ];
    const isFirstItemFocused = focusedOptionIndex === 0;
    const isLastItemFocused = focusedOptionIndex === clickables.length - 1;
    const isShiftKeyPressed = e.shiftKey;

    try {
      switch (keyCode) {
        case ESCAPE_KEY: {
          setIsOpen(false);
          break;
        }

        case TAB_KEY: {
          if (isLastItemFocused && !isShiftKeyPressed) {
            e.preventDefault(); // interrupt the tab key
            firstOption.focus(); // cycle back to the first element
          }

          if (isFirstItemFocused && isShiftKeyPressed) {
            e.preventDefault(); // interrupt the tab key
            lastOption.focus(); // cycle back to the last element
          }
          break;
        }

        case UP_ARROW: {
          if (!isFirstItemFocused) {
            (clickables[focusedOptionIndex - 1] as HTMLElement).focus();
          } else {
            lastOption.focus();
          }
          break;
        }

        case DOWN_ARROW: {
          if (isLastItemFocused) {
            firstOption.focus();
          } else {
            (clickables[focusedOptionIndex + 1] as HTMLElement).focus();
          }
          break;
        }

        default: {
          return;
        }
      }
    } catch (error) {
      console.error(error); // eslint-disable-line
    }
  }, []);

  const { popoverVerticalAlignment, popoverHorizontalAlignment } = useDropdownPopoverAlignment(
    align,
    direction,
  );

  const trigger = React.Children.only(children); // verify we only get a trigger

  return (
    <Popover.Root open={isOpen} onOpenChange={toggle}>
      <StyledActionMenu data-test="action-menu" id={id} ref={menuEl} {...props}>
        <Popover.Trigger asChild style={{ minWidth: 52 }}>
          <div style={{ minWidth: 34 }}>
            {cloneElement(trigger as any, {
              'data-test': 'action-menu-trigger',
              isDisabled,
              onClick: (e?: Event) => {
                e?.stopPropagation();
                setIsOpen(!isOpen);
              },
            })}
          </div>
        </Popover.Trigger>
        <Popover.Portal
          /**
           * Specifying the container element ensures that the popover is rendered within the local
           * DOM context, which ensures it does not interfere with useClickAway logic (ie. when
           * an ActionMenu is used in a Drawer).
           */
          container={menuEl.current}
        >
          <Popover.Content
            asChild
            hideWhenDetached // automatically hide the content when the trigger becomes fully occluded.
            avoidCollisions={automaticallyAvoidCollisions}
            side={popoverVerticalAlignment}
            sideOffset={4}
            align={popoverHorizontalAlignment}
            onOpenAutoFocus={(e) => e.preventDefault()}
          >
            <StyledOptions
              data-test="action-menu-options"
              onClick={() => setIsOpen(false)}
              ref={optionEl}
            >
              {options.map((option: ActionMenuOption) => {
                const { Wrapper = FragmentWrapper } = option;

                if (option.as === AllowedActionMenuElements.Title) {
                  return (
                    <Wrapper key={option.id} data-test={`action-menu-item-${option.id}`}>
                      <TitleOption>{option.content}</TitleOption>
                    </Wrapper>
                  );
                }

                const htmlElement = ReactDOMServer.renderToString(option.content as any);
                const hasIcon =
                  !(typeof option.content === 'string') && htmlElement.includes('svg');
                const optionProps: ActionMenuOptionProps = {
                  isDisabled: !!option.isDisabled,
                  onClick: option.isDisabled ? noop : option.onClick,
                  tabIndex: option.isDisabled ? -1 : 0,
                };
                if (option.as === AllowedActionMenuElements.Link) {
                  const linkOption = option as LinkOption;
                  if (linkOption.href && !option.isDisabled) optionProps.href = linkOption.href;
                  if (linkOption.href && linkOption.isExternalLink && !option.isDisabled) {
                    optionProps.target = '_blank';
                    optionProps.rel = 'noreferrer';
                  }
                  if (linkOption.target && !option.isDisabled)
                    optionProps.target = linkOption.target;
                }

                return (
                  <Wrapper key={option.id}>
                    <StyledOption
                      as={option.as}
                      aria-disabled={option.isDisabled}
                      data-test="action-menu-option"
                      data-id={`option-${option.id}`}
                      data-destructive={option.isDestructive}
                      hasIcon={hasIcon}
                      onKeyDown={handleKeyPress}
                      {...optionProps}
                    >
                      {option.content}
                      {option?.description && (
                        <StyledOptionDescription>{option.description}</StyledOptionDescription>
                      )}
                    </StyledOption>
                    {option.appendDivider && <Divider></Divider>}
                  </Wrapper>
                );
              })}
            </StyledOptions>
          </Popover.Content>
        </Popover.Portal>
      </StyledActionMenu>
    </Popover.Root>
  );
};
